import { apiUrl } from "../config/api"
/**
 * Where a visitor came from — MTC-FCS-038 / 039.
 * ============================================================================
 * A marketer drops an image into a Facebook group. Somebody taps it, lands on
 * `/website?id=roof-grp-04`, and either buys or does not. This is the record
 * of that, kept in the visitor's own browser.
 *
 * Three facts per visit, which is what the brief asked for:
 *
 *     at    when they arrived (ISO)
 *     from  where they came from (document.referrer)
 *     tag   the id on the URL — which image, in which group
 *
 * ...and a status that starts at 1 and becomes 2 if they buy.
 *
 * ── WHY localStorage AND NOT A SERVER CALL ─────────────────────────────────
 * Because the interesting moment is the PURCHASE, which happens minutes later
 * and often after a round trip to Stripe. Holding the tag in the browser means
 * the sale can be attributed without a session, a cookie banner, or a request
 * on every page view. The server hears about it once, when there is something
 * worth saying.
 *
 * ── FIRST TOUCH WINS ───────────────────────────────────────────────────────
 * The first tagged visit is the one credited. Somebody who arrives from an
 * affiliate's post, wanders off, and comes back through a Google search was
 * still won by the affiliate — and crediting the last click would quietly pay
 * whoever happened to be nearest the till.
 *
 * ── IT NEVER THROWS ────────────────────────────────────────────────────────
 * Private windows, blocked storage and full quotas are all ordinary. Tracking
 * that breaks a page is worse than tracking that misses a visit.
 */

const KEY = "fcs.visits.v1"
/** Which codes this browser has already told the server about. */
const SENT_KEY = "fcs.visits.sent.v1"
const MAX = 200

/** 1 visited and did not buy, 2 bought — MTC-FCS-039. */
export const VISIT_STATUS = { visited: 1, purchased: 2 }

/** The query parameters that carry an affiliate's id. `id` is the one we ship. */
const TAG_PARAMS = ["id", "ref", "aff"]

function read() {
  try {
    const raw = window.localStorage.getItem(KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function write(list) {
  try {
    // Newest last, oldest dropped: a browser that has been to the site a
    // thousand times should not carry a thousand rows around.
    window.localStorage.setItem(KEY, JSON.stringify(list.slice(-MAX)))
    return true
  } catch {
    return false
  }
}

/** Cleans a tag so a URL cannot put anything surprising into the log. */
export function cleanTag(value) {
  return String(value || "")
    .trim()
    .replace(/[^A-Za-z0-9_-]/g, "")
    .slice(0, 48)
}

/** The affiliate id on a URL, or "" — `?id=`, `?ref=`, `?aff=`. */
export function tagFromSearch(search) {
  try {
    const params = new URLSearchParams(search || "")
    for (const name of TAG_PARAMS) {
      const found = cleanTag(params.get(name))
      if (found) return found
    }
  } catch {
    /* A malformed query string is not worth an error. */
  }
  return ""
}

/**
 * Where they came from, shortened to something readable.
 *
 * Facebook's in-app browser reports `l.facebook.com` or `m.facebook.com`, and
 * sometimes nothing at all — which is itself worth recording as "direct"
 * rather than as an empty field nobody can interpret later.
 */
export function previousLocation(referrer, here) {
  const raw = String(referrer || "")
  if (!raw) return "direct"
  try {
    const url = new URL(raw)
    // Our own pages are not a source; the first outside referrer is.
    if (here && url.host === here) return "internal"
    return url.host + (url.pathname !== "/" ? url.pathname : "")
  } catch {
    return raw.slice(0, 120)
  }
}

/** Every visit recorded in this browser, oldest first. */
export function visits() {
  return read()
}

/** The tag this visitor should be credited to — the first tagged visit. */
export function creditedTag() {
  const tagged = read().filter(v => v.tag)
  return tagged.length > 0 ? tagged[0].tag : ""
}

/**
 * Records an arrival. Returns the entry, or null when nothing was recorded.
 *
 * A reload or an internal navigation is NOT a new visit: the log is about
 * where people came FROM, and counting a page view as a fresh arrival would
 * make one curious visitor look like ten.
 */
export function recordVisit({ search, referrer, host, now } = {}) {
  if (typeof window === "undefined") return null

  const tag = tagFromSearch(search)
  const from = previousLocation(referrer, host)
  const at = (now instanceof Date ? now : new Date()).toISOString()
  const list = read()

  // Arrived from our own pages with no new tag: the same visit continuing.
  if (from === "internal" && !tag) return null

  const last = list[list.length - 1]
  if (last && last.tag === tag && last.from === from) {
    const sameMinute = Math.abs(new Date(at) - new Date(last.at)) < 60_000
    if (sameMinute) return last
  }

  const entry = {
    at,
    from,
    tag,
    status: VISIT_STATUS.visited,
    // The page they landed on, which says which image was clicked when two
    // point at different places.
    landed: typeof window !== "undefined" ? window.location.pathname : "",
  }

  list.push(entry)
  write(list)
  report(entry, VISIT_STATUS.visited)
  return entry
}

/**
 * Marks the credited visit as a purchase — MTC-FCS-039.
 *
 * Marks the FIRST tagged visit, which is the one being credited, and returns
 * it so a caller can send the attribution on. Called from the success screens,
 * because that is the only place a purchase is a fact rather than an intention.
 */
export function markPurchased(detail = {}) {
  if (typeof window === "undefined") return null
  const list = read()
  const index = list.findIndex(v => v.tag)
  const target = index === -1 ? list.length - 1 : index
  if (target < 0) return null

  list[target] = {
    ...list[target],
    status: VISIT_STATUS.purchased,
    purchasedAt: new Date().toISOString(),
    // What they bought, so an affiliate's report can show more than a count.
    plan: detail.plan || list[target].plan || "",
    site: detail.site || list[target].site || "",
  }
  write(list)
  report(list[target], VISIT_STATUS.purchased, {
    site: list[target].site,
    plan: list[target].plan,
  })
  return list[target]
}

/* ── Telling the server, twice and only twice ───────────────────────────
   The log above is for the visitor's browser. A marketer cannot read that, so
   two moments are reported: the first time this browser sees a code, and the
   moment it buys. Everything else stays local — see src/api/affiliate-event.js
   for why the route is built to be dull to abuse. */

function alreadySent(key) {
  try {
    const raw = window.localStorage.getItem(SENT_KEY)
    const list = raw ? JSON.parse(raw) : []
    if (!Array.isArray(list) || !list.includes(key)) {
      window.localStorage.setItem(SENT_KEY, JSON.stringify([...(list || []), key].slice(-60)))
      return false
    }
    return true
  } catch {
    // Storage blocked: send it. A duplicate visit is a smaller problem than a
    // marketer whose numbers are empty because a browser was in private mode.
    return false
  }
}

/**
 * Fires the beacon. Never awaited, never throws, never blocks a page.
 *
 * `keepalive` matters on the purchase: the success page may navigate or close
 * while the request is in flight, and without it the sale — the one event that
 * decides whether somebody gets paid — is the one most likely to be lost.
 */
function report(entry, status, detail = {}) {
  if (typeof window === "undefined" || !entry || !entry.tag) return
  const once = `${entry.tag}:${status}`
  if (alreadySent(once)) return

  try {
    fetch(apiUrl("affiliate-event"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        code: entry.tag,
        status,
        from: entry.from,
        site: detail.site || "",
        plan: detail.plan || "",
      }),
    }).catch(() => {})
  } catch {
    /* Tracking must never be the reason a page misbehaves. */
  }
}

/** Forgets everything. For the visitor's own use, and for the checks. */
export function clearVisits() {
  try {
    window.localStorage.removeItem(KEY)
    window.localStorage.removeItem(SENT_KEY)
  } catch {
    /* nothing to do */
  }
}
